// Apariencia and Idioma — this browser's own choices (localStorage), like the Mac's per-device
// theme and language. The theme applies on the click; the language too (the Mac needs a restart,
// a page does not: see ./language.ts).
import { useState } from 'react';
import { storedLanguageChoice, t, type LanguageChoice } from '../../../i18n/i18n';
import { applyTheme, storedTheme, type ThemeChoice } from '../../theme';
import { applyLanguageChoice } from './language';
import { RadioRow, SettingsGroup, SwitchRow } from './parts';
import { setGroupByArea, useAreaPrefs } from '../areas/areaPrefs';

const THEMES: ThemeChoice[] = ['system', 'light', 'dark'];

export function AppearanceSection() {
  return (
    <>
      <ThemeGroup />
      <ConversationListGroup />
    </>
  );
}

/** Sistema / Claro / Oscuro — per browser (shared/API.md §41.9: theme is PER DEVICE). */
export function ThemeGroup() {
  const [theme, setTheme] = useState<ThemeChoice>(storedTheme);
  return (
    <SettingsGroup title={t('web.settings.theme')} footer={t('web.settings.localOnly')}>
      <div className="st-themes" role="radiogroup" aria-label={t('web.settings.theme')}>
        {THEMES.map((c) => (
          <button
            key={c}
            type="button"
            role="radio"
            aria-checked={theme === c}
            className={`st-theme${theme === c ? ' checked' : ''}`}
            onClick={() => {
              setTheme(c);
              applyTheme(c, true);
            }}
          >
            <span className={`st-theme-preview ${c}`} aria-hidden>
              <span className="st-theme-side" />
              <span className="st-theme-main">
                <span className="st-theme-bubble a" />
                <span className="st-theme-bubble b" />
              </span>
            </span>
            <span className="st-theme-label">{t(`web.theme.${c}`)}</span>
          </button>
        ))}
      </div>
    </SettingsGroup>
  );
}

/** «Agrupar por área» (shared/API.md §37): a list option, ON by default, kept per browser. */
function ConversationListGroup() {
  const prefs = useAreaPrefs();
  return (
    <SettingsGroup title={t('web.area.listSection')} footer={t('web.area.groupByArea.footer')}>
      <SwitchRow icon="people" title={t('web.area.groupByArea')} on={prefs.groupByArea} onChange={setGroupByArea} />
    </SettingsGroup>
  );
}

export function LanguageSection() {
  const [lang, setLang] = useState<LanguageChoice>(storedLanguageChoice);
  const options: Array<{ value: LanguageChoice; label: string }> = [
    { value: 'system', label: t('profile.language.system') },
    // A language is listed in its OWN name (AppLanguage.endonym).
    { value: 'es', label: 'Español' },
    { value: 'en', label: 'English' },
  ];
  return (
    <SettingsGroup title={t('profile.language.section')} footer={`${t('web.settings.language.live')} ${t('web.settings.localOnly')}`}>
      <div role="radiogroup" aria-label={t('profile.language.section')}>
        {options.map((o) => (
          <RadioRow
            key={o.value}
            title={o.label}
            checked={lang === o.value}
            onPick={() => {
              if (lang === o.value) return;
              setLang(o.value);
              applyLanguageChoice(o.value);
            }}
          />
        ))}
      </div>
    </SettingsGroup>
  );
}
